import { all, call, put, take, takeLatest, select, Effect } from 'redux-saga/effects';
import { INIT_DASHBOARD_REQUEST, SUBSCRIPTION_CHANGED } from './dashboardActionTypes';
import { FETCH_SUBSCRIPTIONS_SUCCESS } from '@modules/subscriptions/core/actionTypes';
import { fetchSubscriptionsRequest } from '@modules/subscriptions/core/actions';
import { fetchCustomerRequest } from '@modules/customer/core/actions';
import { getCustomerSelector } from '@modules/customer/core/selectors';
import { fetchAddressesRequest } from '@modules/addresses/core/actions';
import { fetchPaymentMethodsRequest } from '@modules/payment/core/actions';
import { fetchOrderHistoryRequest } from '@modules/orders/core/actions';
import { fetchOrderScheduleRequest } from '@modules/order-schedule/core/actions';
import { fetchFlowRewardBannerRequest } from '@modules/subscriptions/core/actions';
import { fetchProductRequest } from '@modules/products/core/actions';
import { fetchUpsellsRequest } from '@modules/shop/core/actions';
import { updateSettings } from '@modules/settings/core/actions';
import { getDefaultSubscriptionSelector } from '@modules/subscriptions/core/selectors';
import { VISIBLE_RECIPES } from '@constants/subscription';
import { FetchSubscriptionsSuccess } from '@modules/subscriptions/core/types';

function getDefaultFromSubscriptions(subscriptions: ISubscription[]): ISubscription | null {
  if (!subscriptions.length) return null;
  return subscriptions.find((sub) => sub.status === 'ACTIVE') ?? subscriptions[0];
}

// Fire the subscription-dependent fetches. order_history is on the critical
// path for the current-order tracking card, so we never block it on the
// customer fetch: we pass the cached Shopify id when it's already there and
// otherwise fire immediately and let the API resolve it from Loop.
function* fetchSubscriptionDependentData(subscription: ISubscription) {
  const subscriptionId = subscription.id;
  const lineItem = subscription.lines?.[0];

  // Read whatever Shopify id is already cached, but never wait for the customer
  // fetch -- blocking here was adding a full round-trip to the time the
  // tracking card takes to appear.
  const cachedCustomer: ICustomer | null = yield select(getCustomerSelector);
  const shopifyCustomerId: string | undefined = cachedCustomer?.shopifyId;

  const parallelEffects: Effect[] = [
    put(fetchOrderHistoryRequest({ subscriptionId, shopifyCustomerId })),
    put(fetchOrderScheduleRequest({ subscriptionId })),
    put(fetchFlowRewardBannerRequest({ subscriptionId })),
  ];

  if (lineItem?.productShopifyId) {
    parallelEffects.push(put(fetchProductRequest({ productId: lineItem.productShopifyId })));
  }

  yield all(parallelEffects);
}

function* syncSubscriptionOptions(subscription: ISubscription) {
  const subscriptionOptions = subscription?.lines?.[0]?.product?.options;
  if (!subscriptionOptions) return;

  const subscriptionRecipes = VISIBLE_RECIPES.map((recipe) => recipe.id).filter((recipe) =>
    subscriptionOptions.recipes?.includes(recipe as string),
  );

  yield put(
    updateSettings({
      subscriptionOptions: {
        recipes: subscriptionRecipes,
        cycle: subscriptionOptions?.cycle,
        pension: subscriptionOptions?.pension,
      },
    }),
  );
}

function* initDashboardSaga() {
  yield all([
    put(fetchSubscriptionsRequest()),
    put(fetchCustomerRequest()),
    put(fetchAddressesRequest()),
    put(fetchPaymentMethodsRequest()),
    put(fetchUpsellsRequest()),
  ]);

  const action: FetchSubscriptionsSuccess = yield take(FETCH_SUBSCRIPTIONS_SUCCESS);
  const subscriptions: ISubscription[] = action.payload.subscriptions.data;

  const validSubscriptions = Array.isArray(subscriptions)
    ? subscriptions.filter(
        (sub: ISubscription) =>
          sub.lines?.some((lineItem: any) => lineItem != null && lineItem.product !== null) &&
          sub.pet !== null,
      )
    : [];

  const defaultSubscription = getDefaultFromSubscriptions(validSubscriptions);

  if (defaultSubscription) {
    yield put(updateSettings({ defaultSubscription: defaultSubscription.id }));

    yield call(syncSubscriptionOptions, defaultSubscription);

    yield call(fetchSubscriptionDependentData, defaultSubscription);
  }
}

function* onSubscriptionChangeSaga() {
  const defaultSubscription: ISubscription | undefined = yield select(
    getDefaultSubscriptionSelector,
  );

  if (!defaultSubscription) return;

  yield call(syncSubscriptionOptions, defaultSubscription);
  yield call(fetchSubscriptionDependentData, defaultSubscription);

  yield put(fetchCustomerRequest());
}

export function* dashboardSaga() {
  yield all([
    takeLatest(INIT_DASHBOARD_REQUEST, initDashboardSaga),
    takeLatest(SUBSCRIPTION_CHANGED, onSubscriptionChangeSaga),
  ]);
}
